import { inject, Injectable } from '@angular/core';
import { IsoTimeOffset, type IsoTimeTz } from '../types/iso-date';
import { TimeZoneService } from './time-zone.service';
import { isIsoTimeTz } from '../functions/validation/is-iso-time-tz.function';
import { AbstractTimeService } from './abstract-time.service';

@Injectable({ providedIn: 'root' })
export class TimeTzService extends AbstractTimeService<IsoTimeTz> {
  private timeZoneService = inject(TimeZoneService)

  override now(): IsoTimeTz {
    const local = new Date();
    const timeZone = this.timeZoneService.timeZone();
    return formatTimeWithOffset(local, timeZone) as IsoTimeTz;
  }

  override midnight(): IsoTimeTz {
    const local = new Date(1970, 0, 1, 0, 0, 0, 0); // Jan 1, 1970 at midnight local
    const timeZone = this.timeZoneService.timeZone();
    return formatTimeWithOffset(local, timeZone) as IsoTimeTz;
  }

  override isValid(date: string): date is IsoTimeTz {
    return isIsoTimeTz(date);
  }
}

function formatTimeWithOffset(date: Date, timeZone: string): string {
  const svDate = new Intl.DateTimeFormat('sv-SE', {
    timeZone: timeZone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZoneName: 'longOffset'
  }).format(date);
  // svDate expected format: "YYYY-MM-DD HH:mm:ss GMT±HHMM" or "YYYY-MM-DD HH:mm:ss UTC"
  const [timePart, tzToken] = svDate.split(' ');

  // Normalize timezone token to offset string
  let offset: IsoTimeOffset;
  if (tzToken === 'UTC' || tzToken === 'GMT') {
    offset = 'Z' as IsoTimeOffset;
  } else {
    // Remove leading 'GMT' if present, and optional colon between HH and MM
    const tzAbbr = tzToken.replace(/^GMT/, ''); // e.g., '+0230' or '-1130'
    const m = /^([+-])(\d{2}):?(\d{2})$/.exec(tzAbbr);
    if (m) {
      const [, sign, hh, mm] = m;
      if (hh === '00' && mm === '00') {
        offset = 'Z' as IsoTimeOffset;
      } else {
        offset = `${sign}${hh}:${mm}` as IsoTimeOffset;
      }
    } else {
      // Fallback to Z if not recognized
      offset = 'Z' as IsoTimeOffset;
    }
  }

  const ms = date.getMilliseconds().toString().padStart(3, '0');
  return `T${timePart}.${ms}${offset}`;
}
